import { db } from '@/firebase';
import { capture } from '@/handlers/error';
import { doc, getDoc, onSnapshot, updateDoc } from 'firebase/firestore';
import React, { createContext, PropsWithChildren, useContext, useEffect, useState } from 'react';
import { useIdentity } from '../identity/IdentityProvider';

export interface ExpenseSettings {
    lineApprovalEnabled?: boolean;
    groupApprovalEnabled?: boolean;
    allowInvalidInvoiceEnabled?: boolean;
    categories?: string[];
}

export interface CurrencySettings {
    defaultCurrency: string;
    exchangeRates?: Record<string, number>;
}

export interface B1Settings {
    url?: string;
    db?: string;
    username?: string;
    secret?: string;
}

type ExpenseSettingContextType = ExpenseSettings;
type CurrencySettingContextType = CurrencySettings;
type B1SettingContextType = B1Settings & { update: (newSettings: B1Settings) => void };

const ExpenseSettingContext = createContext<ExpenseSettingContextType | undefined>(undefined);
const CurrencySettingContext = createContext<CurrencySettingContextType | undefined>(undefined);
const B1SettingContext = createContext<B1SettingContextType | undefined>(undefined);

export const SettingProvider: React.FC<PropsWithChildren> = ({ children }) => {
    const { organization } = useIdentity();

    const [expense, setExpense] = useState<ExpenseSettings>({});
    const [exchangeRates, setExchangeRates] = useState<Record<string, number>>({});
    const [b1Settings, setB1Settings] = useState<B1Settings | undefined>(undefined);

    const updateB1Settings = (newSettings: B1Settings) => {
        setB1Settings((prev) => ({
            ...prev,
            ...newSettings,
        }));

        updateDoc(doc(db, `organizations/${organization?.oid}/settings/b1`), { ...newSettings }).catch((error) => {
            capture(error);
        });
    };

    useEffect(() => {
        if (!organization) {
            return;
        }

        const unsub = onSnapshot(doc(db, `organizations/${organization.oid}/settings/expense`), (doc) => {
            const setting = doc.data() as ExpenseSettings;

            if (setting.lineApprovalEnabled === undefined) {
                setting.lineApprovalEnabled = true;
            }

            if (setting.groupApprovalEnabled === undefined) {
                setting.groupApprovalEnabled = true;
            }

            if (setting.allowInvalidInvoiceEnabled === undefined) {
                setting.allowInvalidInvoiceEnabled = false;
            }

            setExpense(setting);
        });

        return () => {
            unsub();
        };
    }, [organization]);

    useEffect(() => {
        if (!organization) {
            return;
        }

        const unsub = onSnapshot(doc(db, `organizations/${organization.oid}/settings/b1`), (doc) => {
            setB1Settings(
                (prev) =>
                    ({
                        ...prev,
                        ...doc.data(),
                    }) as B1Settings
            );
        });

        getDoc(doc(db, `organizations/${organization.oid}/settings/b1/system/password`)).then((doc) => {
            if (doc.exists()) {
                const data = doc.data();
                if (data.secret && typeof data.secret === 'string') {
                    setB1Settings((prev) => ({
                        ...prev,
                        secret: data.secret,
                    }));
                }
            }
        });

        return () => {
            unsub();
        };
    }, [organization]);

    useEffect(() => {
        async function fetchWithFallback(urls: string[], options?: RequestInit): Promise<Response> {
            // Loop through each URL sequentially
            for (const url of urls) {
                try {
                    const response = await fetch(url, options);

                    // Crucial: fetch only throws on network failure,
                    // so we must manually check for HTTP error statuses.
                    if (response.ok) {
                        return response;
                    }

                    console.warn(`Fetch failed for ${url} with status: ${response.status}`);
                } catch (error) {
                    // Handles network dropouts, CORS issues, or bad domains
                    console.warn(`Network error fetching from ${url}:`, error instanceof Error ? error.message : String(error));
                }
            }

            // If the loop finishes, all URLs failed
            throw new Error('All URL fetch attempts failed.');
        }

        fetchWithFallback([
            'https://cdn.jsdelivr.net/npm/@fawazahmed0/currency-api@latest/v1/currencies/hkd.min.json',
            'https://latest.currency-api.pages.dev/v1/currencies/hkd.min.json',
            'https://cdn.jsdelivr.net/npm/@fawazahmed0/currency-api@latest/v1/currencies/hkd.json',
            'https://latest.currency-api.pages.dev/v1/currencies/hkd.json',
        ])
            .then((response) => response.json())
            .then((data) => {
                const rates: Record<string, number> = data.hkd;
                for (const [currency, rate] of Object.entries(rates)) {
                    if (typeof rate === 'number') {
                        rates[currency] = 1 / rate;
                    }
                }
                setExchangeRates(rates);
            });
    }, []);

    return (
        <ExpenseSettingContext.Provider
            value={{
                ...expense,
            }}
        >
            <B1SettingContext.Provider
                value={{
                    ...b1Settings,
                    update: updateB1Settings,
                }}
            >
                <CurrencySettingContext.Provider
                    value={{
                        defaultCurrency: 'HKD',
                        exchangeRates,
                    }}
                >
                    {children}
                </CurrencySettingContext.Provider>
            </B1SettingContext.Provider>
        </ExpenseSettingContext.Provider>
    );
};

export const useExpenseSetting = (): ExpenseSettingContextType => {
    const context = useContext(ExpenseSettingContext);
    if (!context) {
        throw new Error('useExpenseSetting must be used within an SettingProvider');
    }

    return context;
};

export const useB1Setting = (): B1SettingContextType => {
    const context = useContext(B1SettingContext);
    if (!context) {
        throw new Error('useB1Setting must be used within an SettingProvider');
    }

    return context;
};

export const useCurrencySetting = (): CurrencySettingContextType => {
    const context = useContext(CurrencySettingContext);
    if (!context) {
        throw new Error('useCurrencySetting must be used within an SettingProvider');
    }

    return context;
};
